import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';

import { useAuth } from '@/src/app/context/useAuth';
import { Container, ErrorMessage } from '@/src/ui';
import { apiRequest } from '@/src/services';
import HandscoreQuestionsTable from './components/HandscoreQuestionsTable';
import type { QuestionDTO, StudentExamAttemptDTO } from './models/handscoreDTOs';
import type { FilterState } from './models/filterDTOs';
import HandscoreQuestionsFilters from './components/HandscoreQuestionsFilters';
import { Tab, Tabs } from '@mui/material';

const HandscoreQuestionsPage: React.FC = () => {
    document.title = 'Handscore Questions';

    const { user } = useAuth();
    const userId = user?.id || 'nouser';
    const UNIVERSAL_FILTER_KEY = `universal-filters-${userId}`;

    const [searchString, setSearchString] = useState('');
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState<'questions' | 'students'>('questions');
    const [questions, setQuestions] = useState<QuestionDTO[]>([]);
    const [studentAttempts, setStudentAttempts] = useState<StudentExamAttemptDTO[]>([]);
    const [filtersSubmitted, setFiltersSubmitted] = useState(false);

    const [filterState, setFilterState] = useState<FilterState>(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    districtId: parsed.districtId || 0,
                    schoolId: parsed.schoolId || 0,
                    gradeLevelMasterId: parsed.gradeLevelMasterId || 0,
                    classId: parsed.classId || 0,
                    examTypeMasterId: 0,
                    examId: 0,
                };
            }
        } catch {
            // intentionally empty
        }
        return {
            districtId: 0,
            schoolId: 0,
            gradeLevelMasterId: 0,
            classId: 0,
            examTypeMasterId: 0,
            examId: 0,
        };
    });

    const refreshTableData = useCallback(
        async (filters: FilterState) => {
            setError('');

            try {
                let baseUrl = 'assessments/studentExamAttempts/handscoreAttemptStatus';

                if (activeTab === 'students') {
                    baseUrl += '?groupBy=student';
                    baseUrl += `&gradeLevelMasterId=${filters.gradeLevelMasterId}`;
                    baseUrl += `&schoolId=${filters.schoolId}`;
                    baseUrl += `&classId=${filters.classId}`;
                    baseUrl += `&examTypeMasterId=${filters.examTypeMasterId}`;
                    baseUrl += `&examId=${filters.examId}`;

                    const res = await apiRequest<StudentExamAttemptDTO[]>('GET', baseUrl);
                    setStudentAttempts(res.data);
                } else if (activeTab === 'questions') {
                    baseUrl += '?groupBy=question';
                    baseUrl += `&gradeLevelMasterId=${filters.gradeLevelMasterId}`;
                    baseUrl += `&schoolId=${filters.schoolId}`;
                    baseUrl += `&classId=${filters.classId}`;
                    baseUrl += `&examTypeMasterId=${filters.examTypeMasterId}`;
                    baseUrl += `&examId=${filters.examId}`;

                    const res = await apiRequest<QuestionDTO[]>('GET', baseUrl);
                    setQuestions(res.data);
                }

                setFiltersSubmitted(true);
            } catch {
                setError('Failed to fetch handscore data');
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filterState, activeTab]
    );

    const updateQuestionCorrectStatus = useCallback(
        (attemptQuestionId: number, studentUserId: number, isCorrect: boolean) => {
            if (activeTab === 'questions') {
                setQuestions((prevQuestions) =>
                    prevQuestions.map((question) => {
                        const updatedStudents = question.students.map((student) =>
                            student.studentUserId === studentUserId &&
                            student.attemptQuestionId === attemptQuestionId
                                ? { ...student, isCorrect, isScored: true }
                                : student
                        );
                        return { ...question, students: updatedStudents };
                    })
                );
            } else if (activeTab === 'students') {
                if (!studentUserId) return;
                setStudentAttempts((prevAttempts) =>
                    prevAttempts.map((attempt) => {
                        if (attempt.studentUserId === studentUserId) {
                            const updatedQuestions = attempt.questions.map((question) =>
                                question.attemptQuestionId === attemptQuestionId
                                    ? { ...question, isCorrect, isScored: true }
                                    : question
                            );
                            return { ...attempt, questions: updatedQuestions };
                        }
                        return attempt;
                    })
                );
            }
        },
        [activeTab]
    );

    useEffect(() => {
        if (!filterState || !filterState.districtId || filterState.districtId === 0) {
            return;
        }
        refreshTableData(filterState);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    useEffect(() => {
        if (!userId) return;
        const universalFilters = {
            gradeLevelMasterId: filterState.gradeLevelMasterId,
            districtId: filterState.districtId,
            schoolId: filterState.schoolId,
            classId: filterState.classId,
        };
        try {
            localStorage.setItem(UNIVERSAL_FILTER_KEY, JSON.stringify(universalFilters));
        } catch {
            // intentionally empty
        }
    }, [filterState, userId, UNIVERSAL_FILTER_KEY]);

    return (
        <Container>
            <h1 className="page-header">Handscore Questions</h1>
            <br />

            <HandscoreQuestionsFilters
                refreshTableData={refreshTableData}
                filterState={filterState}
                setFilterState={setFilterState}
                setSearchString={setSearchString}
            />

            <ErrorMessage error={error} />
            <br />

            {filtersSubmitted && (
                <>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                        <Tabs
                            value={activeTab}
                            onChange={(_, newValue) => setActiveTab(newValue)}
                            aria-label="Handscore tabs"
                            textColor="primary"
                            indicatorColor="primary"
                            sx={{
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    minWidth: 120,
                                    mr: 2,
                                    borderRadius: '8px 8px 0 0',
                                },
                                '& .Mui-selected': {
                                    backgroundColor: 'rgba(103, 58, 183, 0.1)', // light purple background
                                    fontWeight: 600,
                                },
                            }}
                        >
                            <Tab label="BY QUESTION" value="questions" />
                            <Tab label="BY STUDENT ATTEMPT" value="students" />
                        </Tabs>
                    </Box>
                    <HandscoreQuestionsTable
                        questions={questions}
                        studentExamAttempts={studentAttempts}
                        updateQuestionCorrectStatus={updateQuestionCorrectStatus}
                        searchString={searchString}
                        activeTab={activeTab}
                    />
                </>
            )}
        </Container>
    );
};

export default HandscoreQuestionsPage;
